import React, { useState, useEffect, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
// Reuses ProductAutocomplete's stylesheet rather than duplicating its rules
// under new names. The classes are visually generic - a search input and a
// dropdown - even though their names say "product".
import './ProductAutocomplete.css';

/**
 * EmployeeAutocomplete
 * Employee picker for the payroll form. Filters an already-loaded array
 * client-side - the payroll API is admin-only and the active roster is a
 * few dozen rows, so there is no reason to hit the API per keystroke.
 *
 * Simpler than ProductAutocomplete on purpose: that one positions its menu
 * with `fixed` because it lives inside a scrolling table wrapper that clips
 * absolute children. This one sits in an ordinary form, so the stylesheet's
 * default positioning is enough.
 */
const EmployeeAutocomplete = ({
  employees = [],
  onSelect,
  selectedEmployee = null,
  disabled = false
}) => {
  const { t } = useTranslation();
  // Stable id so the combobox input can point aria-controls at its listbox.
  const listboxId = useId();
  const [searchInput, setSearchInput] = useState(selectedEmployee?.full_name || '');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const containerRef = useRef(null);

  const query = searchInput.trim().toLowerCase();
  const suggestions = query
    ? employees.filter((employee) =>
        (employee.full_name || '').toLowerCase().includes(query))
    : [];

  useEffect(() => {
    setSearchInput(selectedEmployee?.full_name || '');
  }, [selectedEmployee?.full_name]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (employee) => {
    setSearchInput(employee.full_name);
    setShowSuggestions(false);
    // The whole employee, not just the id: the form needs daily_salary to
    // compute the base salary and the bank fields to snapshot them.
    onSelect(employee);
  };

  return (
    <div className="product-search-container" ref={containerRef}>
      <input
        id="payroll-employee"
        type="text"
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => query && setShowSuggestions(true)}
        placeholder={t('payroll.selectEmployee')}
        disabled={disabled}
        className="product-search-input"
        role="combobox"
        aria-label={t('payroll.employee')}
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={listboxId}
      />

      {showSuggestions && suggestions.length > 0 && (
        <div id={listboxId} className="product-search-suggestions" role="listbox">
          {suggestions.map((employee) => (
            <div
              key={employee.id}
              onClick={() => handleSelect(employee)}
              className="product-suggestion-item"
              role="option"
              aria-selected={selectedEmployee?.id === employee.id}
            >
              {employee.full_name}
            </div>
          ))}
        </div>
      )}

      {showSuggestions && query && suggestions.length === 0 && (
        <div className="product-search-empty" role="status">
          {t('common.noResults')}
        </div>
      )}
    </div>
  );
};

export default EmployeeAutocomplete;
